import {
  Autocomplete,
  Button,
  type Item,
  SelectInput,
  SelectInputItem,
  TextField,
} from '@ads/components-react';
import {
  AdjustmentsHorizontalIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DropDownListViewModel, DropDownRepositoryResponse } from '@/shared/api/generated';
import { DatePicker } from '@/shared/ui/atoms';
import { getAutocompleteSearchMessage } from '@/shared/utils/getAutocompleteSearchMessage';

import type { QuestionFilters as Filters } from '../../../store/atoms/questionFiltersAtom';

// ─── Tipos ────────────────────────────────────────────────────────────────────

/** Props do componente QuestionFilters */
export interface QuestionFiltersProps {
  /** Estado atual dos filtros */
  readonly filters: Filters;
  /** Opções para o autocomplete de Disciplina (DropDownRepositoryResponse: key=ID, value=nome) */
  readonly disciplineOptions: DropDownRepositoryResponse[];
  /** Opções para o select de Autor (DropDownRepositoryResponse: key=ID, value=nome) */
  readonly authorOptions: DropDownRepositoryResponse[];
  /** Opções para o select de Empresa (DropDownRepositoryResponse: key=ID, value=nome) */
  readonly companyOptions: DropDownRepositoryResponse[];
  /** Opções para o select de Tipo de Componente (DropDownListViewModel: key=ID, value=nome) */
  readonly componentTypeOptions: DropDownListViewModel[];
  /** Callback ao alterar qualquer filtro */
  readonly onFiltersChange: (partial: Partial<Filters>) => void;
  /** Callback ao clicar em Pesquisar */
  readonly onSearch: () => void;
  /** Callback ao limpar todos os filtros */
  readonly onClear: () => void;
  /** Callback ao digitar no autocomplete de disciplina — para busca server-side com debounce */
  readonly onDisciplineSearchChange?: (value: string) => void;
  /** Indica se a última busca de disciplinas foi concluída com sucesso — controla exibição do "não encontrado" */
  readonly isDisciplineQuerySuccess?: boolean;
}

const LANGUAGE_OPTIONS = [
  { value: 'pt-BR', labelKey: 'language.pt-BR' as const },
  { value: 'en-US', labelKey: 'language.en-US' as const },
  { value: 'es-ES', labelKey: 'language.es-ES' as const },
];

const EMPTY_PENDING = {
  disciplineId: null as number | null,
  personId: null as number | null,
  companyId: null as number | null,
  componentType: null as number | null,
  cultureInfo: null as string | null,
  labelSearch: '',
  startDate: '',
  endDate: '',
};

// ─── Componente principal ─────────────────────────────────────────────────────

/**
 * Organism QuestionFilters — painel de filtros da Biblioteca de Questões.
 *
 * Exibe campos de filtro (disciplina, autor, empresa, datas, tipo) em
 * um layout de grid responsivo. Não gerencia estado — recebe e emite via props.
 *
 * @example
 * ```tsx
 * <QuestionFilters
 *   filters={filters}
 *   disciplineOptions={disciplineOptions}
 *   authorOptions={authorOptions}
 *   companyOptions={companyOptions}
 *   componentTypeOptions={componentTypeOptions}
 *   onFiltersChange={updateFilters}
 *   onSearch={() => updateFilters({ page: 1 })}
 *   onClear={resetFilters}
 * />
 * ```
 */
export function QuestionFilters({
  filters,
  disciplineOptions,
  authorOptions,
  companyOptions,
  componentTypeOptions,
  onFiltersChange,
  onSearch,
  onClear,
  onDisciplineSearchChange,
  isDisciplineQuerySuccess = false,
}: QuestionFiltersProps) {
  const { t } = useTranslation('questionLibrary');
  const { t: tCommon } = useTranslation();

  const [resetKey, setResetKey] = useState(0);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [disciplineInputValue, setDisciplineInputValue] = useState('');

  const [pending, setPending] = useState({
    disciplineId: filters.disciplineId,
    personId: filters.personId,
    companyId: filters.companyId,
    componentType: filters.componentType,
    cultureInfo: filters.cultureInfo,
    labelSearch: filters.labelSearch,
    startDate: filters.startDate,
    endDate: filters.endDate,
  });

  // Sincroniza estado local quando o pai reseta os filtros (ex: onClear)
  useEffect(() => {
    setPending({
      disciplineId: filters.disciplineId,
      personId: filters.personId,
      companyId: filters.companyId,
      componentType: filters.componentType,
      cultureInfo: filters.cultureInfo,
      labelSearch: filters.labelSearch,
      startDate: filters.startDate,
      endDate: filters.endDate,
    });
  }, [
    filters.disciplineId,
    filters.personId,
    filters.companyId,
    filters.componentType,
    filters.cultureInfo,
    filters.labelSearch,
    filters.startDate,
    filters.endDate,
  ]);

  const hasPendingFilters =
    pending.disciplineId !== null ||
    pending.personId !== null ||
    pending.companyId !== null ||
    pending.componentType !== null ||
    pending.cultureInfo !== null ||
    pending.labelSearch !== '' ||
    pending.startDate !== '' ||
    pending.endDate !== '';

  function handleSearch() {
    onFiltersChange({ ...pending, page: 1 });
    onSearch();
  }

  function handleClear() {
    setResetKey(k => k + 1);
    setPending(EMPTY_PENDING);
    setDisciplineInputValue('');
    onDisciplineSearchChange?.('');
    onClear();
  }

  const disciplineItems = disciplineOptions.map(
    (item): Item<string> => ({
      label: item.value ?? '',
      value: String(item.key ?? ''),
    }),
  );

  const authorItems = authorOptions.map(
    (item): Item<string> => ({
      label: item.value ?? '',
      value: String(item.key ?? ''),
    }),
  );

  const companyItems = companyOptions.map(
    (item): Item<string> => ({
      label: item.value ?? '',
      value: String(item.key ?? ''),
    }),
  );

  const selectedDiscipline = disciplineItems.find(
    o => o.value === (pending.disciplineId !== null ? String(pending.disciplineId) : ''),
  );

  const selectedAuthor = authorItems.find(
    o => o.value === (pending.personId !== null ? String(pending.personId) : ''),
  );

  const selectedCompany = companyItems.find(
    o => o.value === (pending.companyId !== null ? String(pending.companyId) : ''),
  );

  const disciplineMessage = getAutocompleteSearchMessage(
    disciplineInputValue.length,
    isDisciplineQuerySuccess,
    disciplineItems.length,
    {
      minChars: tCommon('search.minChars'),
      searching: tCommon('search.searching'),
      notFound: tCommon('search.notFound'),
    },
  );

  return (
    <div className="rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-sm shadow-level1">
      <div className="mb-mini">
        <TextField
          key={`labelSearch-${resetKey}`}
          label={t('filters.labelSearch')}
          placeholder={t('filters.labelSearchPlaceholder')}
          value={pending.labelSearch}
          fullWidth
          size="md"
          onChange={e => setPending(p => ({ ...p, labelSearch: e.target.value }))}
        />
      </div>

      {showAdvanced && (
        <div className="grid grid-cols-1 gap-mini sm:grid-cols-3 mb-mini">
          <Autocomplete<string>
            key={`discipline-${resetKey}`}
            size="md"
            label={t('filters.discipline')}
            list={disciplineItems}
            custom={{ optionList: '' }}
            {...(disciplineMessage !== undefined ? { notFoundMessage: disciplineMessage } : {})}
            {...(selectedDiscipline !== undefined ? { selectedItem: selectedDiscipline } : {})}
            handleChange={item => {
              setPending(p => ({
                ...p,
                disciplineId: item.value ? Number(item.value) : null,
              }));
              setDisciplineInputValue('');
              onDisciplineSearchChange?.('');
            }}
            onChange={e => {
              setDisciplineInputValue(e.target.value);
              onDisciplineSearchChange?.(e.target.value);
            }}
            handleClearQuery={() => {
              setDisciplineInputValue('');
              onDisciplineSearchChange?.('');
              setPending(p => ({ ...p, disciplineId: null }));
            }}
            placeholder={t('filters.disciplinePlaceholder')}
          />

          <Autocomplete<string>
            key={`author-${resetKey}`}
            size="md"
            label={t('filters.author')}
            list={authorItems}
            {...(selectedAuthor !== undefined ? { selectedItem: selectedAuthor } : {})}
            handleChange={item =>
              setPending(p => ({
                ...p,
                personId: item.value ? Number(item.value) : null,
              }))
            }
            placeholder={t('filters.authorPlaceholder')}
          />

          <Autocomplete<string>
            key={`company-${resetKey}`}
            size="md"
            label={t('filters.company')}
            list={companyItems}
            {...(selectedCompany !== undefined ? { selectedItem: selectedCompany } : {})}
            handleChange={item =>
              setPending(p => ({
                ...p,
                companyId: item.value ? Number(item.value) : null,
              }))
            }
            placeholder={t('filters.companyPlaceholder')}
          />

          <DatePicker
            key={`startDate-${resetKey}`}
            label={t('filters.startDate')}
            id="filter-start-date"
            value={pending.startDate}
            onChange={iso => setPending(p => ({ ...p, startDate: iso }))}
          />

          <DatePicker
            key={`endDate-${resetKey}`}
            label={t('filters.endDate')}
            id="filter-end-date"
            value={pending.endDate}
            onChange={iso => setPending(p => ({ ...p, endDate: iso }))}
          />

          <SelectInput
            key={`type-${resetKey}`}
            size="md"
            id="filter-type"
            label={t('filters.type')}
            fullWidth
            value={pending.componentType !== null ? String(pending.componentType) : ''}
            placeholder={t('filters.typePlaceholder')}
            onValueChange={(value: string) =>
              setPending(p => ({
                ...p,
                componentType: value ? Number(value) : null,
              }))
            }
          >
            {componentTypeOptions.map(item => (
              <SelectInputItem
                key={item.key}
                value={String(item.key ?? '')}
                custom={{ root: 'text-nowrap' }}
              >
                {item.value ?? ''}
              </SelectInputItem>
            ))}
          </SelectInput>

          <SelectInput
            key={`cultureInfo-${resetKey}`}
            size="md"
            id="filter-language"
            label={t('filters.language')}
            fullWidth
            value={pending.cultureInfo ?? ''}
            placeholder={t('filters.languagePlaceholder')}
            onValueChange={(value: string) =>
              setPending(p => ({ ...p, cultureInfo: value || null }))
            }
          >
            {LANGUAGE_OPTIONS.map(opt => (
              <SelectInputItem key={opt.value} value={opt.value} custom={{ root: 'text-nowrap' }}>
                {t(opt.labelKey)}
              </SelectInputItem>
            ))}
          </SelectInput>
        </div>
      )}

      <div className="flex gap-mini items-center">
        <Button
          hierarchy="secondary"
          size="md"
          leadingIcon={showAdvanced ? ChevronUpIcon : AdjustmentsHorizontalIcon}
          onClick={() => setShowAdvanced(v => !v)}
        >
          {showAdvanced ? t('filters.lessFilters') : t('filters.moreFilters')}
        </Button>

        <Button
          hierarchy="primary"
          size="md"
          leadingIcon={MagnifyingGlassIcon}
          onClick={handleSearch}
        >
          {t('actions.search')}
        </Button>

        {hasPendingFilters && (
          <Button hierarchy="secondary" size="md" leadingIcon={XMarkIcon} onClick={handleClear}>
            {t('actions.clearFilters')}
          </Button>
        )}
      </div>
    </div>
  );
}
